import {create} from 'zustand';
import useAiWidgetStore from './useAiWidgetStore';

// Simple zustand store for managing active chat windows
// API used by ChatWidget:
// - init(initialChats = [])
// - cleanup()
// - activeChats (array)
// - addChat(chat)
// - updateChat(id, partial)
// - removeChat(id)
// - setActiveChats(chats)
// - setSelectedChat(id)
// - clear()

const useChatStore = create((set, get) => ({
    activeChats: [],
    activeChatsById: {},
    selectedChatId: null,
    isInitialized: false,
    _unsubscribe: null,
    // transient focus request object: { id, token }
    focusRequest: null,
    _focusToken: 0,

    // Initialize the store (idempotent). Optionally accepts an initial array of chats.
    init: (initialChats = []) => {
        const {isInitialized} = get();
        if (isInitialized) return;

        const byId = {};
        const ordered = [];

        (initialChats || []).forEach((c) => {
            if (c && c.id) {
                if (!byId[c.id]) {
                    byId[c.id] = c;
                    ordered.push(c);
                } else {
                    // merge if duplicate id provided
                    byId[c.id] = {...byId[c.id], ...c};
                }
            }
        });

        set({
            activeChats: ordered,
            activeChatsById: byId,
            isInitialized: true,
        });
    },

    // Cleanup any subscriptions/timers. Idempotent.
    cleanup: () => {
        const unsub = get()._unsubscribe;
        if (typeof unsub === 'function') {
            try {
                unsub();
            } catch (e) {
                // swallow
                // console.warn('useChatStore cleanup error', e);
            }
        }
        set({_unsubscribe: null, isInitialized: false});
    },

    // Add a chat. If chat with same id exists, merge/update it.
    addChat: (chat) => {
        if (!chat || !chat.id) return false;
        const {activeChatsById, activeChats} = get();
        const exists = activeChatsById && activeChatsById[chat.id];
        if (exists) {
            const merged = {...activeChatsById[chat.id], ...chat};
            const newById = {...activeChatsById, [chat.id]: merged};
            const newActive = activeChats.map((c) => (c.id === chat.id ? merged : c));
            set({activeChats: newActive, activeChatsById: newById, selectedChatId: chat.id});
            return true;
        }

        // Prepend newest chats so most-recent appear first in UI
        const newActive = [chat, ...activeChats];
        const newById = {...(activeChatsById || {}), [chat.id]: chat};
        set({activeChats: newActive, activeChatsById: newById, selectedChatId: chat.id});
        return true;
    },

    // Request that the message input for a specific chat gains focus.
    // This is transient and includes a token so repeated requests for the same chat cause updates.
    requestFocusFor: (id) => {
        if (!id) return;
        const token = (get()._focusToken || 0) + 1;
        set({_focusToken: token, focusRequest: {id, token}});
    },

    /** Open a 1:1 chat in the CommunicationHub Team tab (floating ChatWindow). */
    openChatInHub: (chat, targetUser, options = {}) => {
        if (!chat?.id) return false;
        let enriched = chat;
        const uid = targetUser?.uid || targetUser?.id || targetUser?.userId || null;
        if (uid) {
            const headshot = targetUser.headshot || targetUser.profilePicture || null;
            const meta = {
                uid,
                displayName: targetUser.displayName || targetUser.userDisplayName || null,
                headshot,
                profilePicture: headshot,
                email: targetUser.email || null,
                handle: targetUser.handle || null,
            };
            enriched = {
                ...chat,
                [`participant_${uid}`]: {
                    ...(chat[`participant_${uid}`] || {}),
                    ...Object.fromEntries(Object.entries(meta).filter(([, v]) => v != null)),
                },
            };
        }
        const parentId = options?.parentId || null;
        const existing = get().activeChatsById?.[chat.id];
        // Tag only newly opened windows so an already-open inbox chat is not owned by this modal.
        if (parentId && !existing) {
            enriched = {...enriched, parentId};
        }
        get().addChat(enriched);
        get().requestFocusFor(chat.id);
        useAiWidgetStore.getState().setActiveTab('team');
        return true;
    },

    /** Close windows that were opened as children of a specific parent (e.g. Collection assignee modal). */
    closeChatsByParent: (parentId) => {
        if (!parentId) return 0;
        const {activeChats} = get();
        const toRemove = (activeChats || []).filter((c) => c?.parentId === parentId);
        toRemove.forEach((c) => get().removeChat(c.id));
        return toRemove.length;
    },

    // Update an existing chat by id with partial fields. Returns false if not found.
    updateChat: (id, partial = {}) => {
        if (!id) return false;
        const {activeChatsById, activeChats} = get();
        if (!activeChatsById || !activeChatsById[id]) return false;
        const updated = {...activeChatsById[id], ...partial};
        const newById = {...activeChatsById, [id]: updated};
        const newActive = activeChats.map((c) => (c.id === id ? updated : c));
        set({activeChats: newActive, activeChatsById: newById});
        return true;
    },

    // Remove a chat. Idempotent.
    removeChat: (id) => {
        if (!id) return false;
        const {activeChatsById, activeChats, selectedChatId} = get();
        if (!activeChatsById || !activeChatsById[id]) return false;
        const newById = {...activeChatsById};
        delete newById[id];
        const newActive = activeChats.filter((c) => c.id !== id);
        const newSelected = selectedChatId === id ? null : selectedChatId;
        set({activeChats: newActive, activeChatsById: newById, selectedChatId: newSelected});
        return true;
    },

    // Replace the active chats list (dedupe by id, order preserved)
    setActiveChats: (chats = []) => {
        const byId = {};
        const ordered = [];
        (chats || []).forEach((c) => {
            if (c && c.id && !byId[c.id]) {
                byId[c.id] = c;
                ordered.push(c);
            } else if (c && c.id) {
                byId[c.id] = {...byId[c.id], ...c};
            }
        });
        set({activeChats: ordered, activeChatsById: byId});
    },

    setSelectedChat: (id) => {
        set({selectedChatId: id || null});
    },

    clear: () => {
        set({activeChats: [], activeChatsById: {}, selectedChatId: null});
    },

    // internal helper to set an unsubscribe function (not exposed in public API but kept here if needed)
    _setUnsubscribe: (fn) => set({_unsubscribe: fn}),
}));

export default useChatStore;
